import React, { Children, isValidElement, ReactNode } from "react";
import { Tooltip, TooltipProps, theme } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { ValueFormatter } from "src/utils/formatters";
import styles from "pages/[orgId]/explore/lib/tooltips/Tooltip.module.css";
import { Tooltip as AntdTooltip } from "antd";

type TooltipSize = "small" | "medium" | "large";

//there is a nice pattern that we are not using for tooltips.
//the Charts use a custom tooltip component that is passed in as a prop.
//we explore teh benefits of this.

interface CustomTooltipProps
  extends Omit<TooltipProps, "overlayClassName" | "overlayInnerStyle"> {
  children: React.ReactNode;
  content: React.ReactNode;
  size?: "default" | "medium" | "large";
  maxHeight?: number;
  mouseEnterDelay?: number;
  mouseLeaveDelay?: number;
  placement?: TooltipProps["placement"];
  className?: string;
  overlayClassName?: string;
  showIcon?: boolean;
}

const sizeConfig: Record<
  TooltipSize,
  {
    padding: string;
    fontSize: number;
    maxWidth: number;
    minWidth: number;
    maxHeight: number;
    borderRadius: number;
  }
> = {
  small: {
    padding: "12px",
    fontSize: 13,
    maxWidth: 240,
    minWidth: 180,
    borderRadius: 12,
    maxHeight: 300,
  },
  medium: {
    padding: "12px",
    fontSize: 14,
    maxWidth: 340,
    minWidth: 280,
    borderRadius: 12,
    maxHeight: 300,
  },
  large: {
    padding: "12px",
    fontSize: 15,
    maxWidth: 600,
    minWidth: 400,
    borderRadius: 14,
    maxHeight: 400,
  },
};

function renderSafeNode(node: ReactNode): ReactNode {
  try {
    // Handle primitives
    if (
      typeof node === "string" ||
      typeof node === "number" ||
      typeof node === "boolean"
    ) {
      return typeof node === "number"
        ? ValueFormatter.format(node, { type: "count" })
        : node;
    }

    // Handle null/undefined
    if (node == null) {
      return null;
    }

    // Handle arrays
    if (Array.isArray(node)) {
      return node.map(renderSafeNode);
    }

    // Handle valid React elements
    if (isValidElement(node)) {
      return React.cloneElement(node as React.ReactElement<any>, {
        children: Children.map(node.props.children, renderSafeNode),
      });
    }

    // Handle objects (including {name, value, uuid})
    if (typeof node === "object") {
      const obj = node as any;

      // If we have a value and it looks like a count/number
      if ("value" in obj) {
        const numValue = Number(obj.value);
        if (!Number.isNaN(numValue)) {
          return ValueFormatter.format(numValue, { type: "count" });
        }
        return String(obj.value);
      }

      if ("name" in obj) return String(obj.name);
      return String(obj);
    }

    // Fallback
    return String(node);
  } catch {
    /*
    console.error('Error rendering node:', error);
    console.error('Problematic node:', node);
    return 'Error rendering content';
    */
    return "Error rendering content";
  }
}

/* doing this because I needed a way to inject colors and the css var were not working */
export function CustomTooltip({
  children,
  content,
  size = "default",
  mouseEnterDelay = 0.5,
  mouseLeaveDelay = 0.5,
  placement = "right",
  className,
  overlayClassName,
  showIcon = false,
  ...otherProps
}: CustomTooltipProps) {
  const { token } = theme.useToken();
  const currentSize = size === "default" ? "small" : size;
  const config = sizeConfig[currentSize as TooltipSize];

  // Generate a unique key for this tooltip
  const tooltipKey = React.useMemo(() => {
    return Math.random().toString(36).slice(2, 9);
  }, [content]);

  const handleVisibleChange = (visible: boolean) => {
    if (!visible) {
      // Reset scroll position when tooltip closes
      const tooltipContent = document.querySelector(".tooltip-content");
      if (tooltipContent) {
        tooltipContent.scrollTop = 0;
      }
    }
  };

  const tooltipContent = (
    <div
      key={tooltipKey}
      className={styles.tooltip_content}
      style={{
        // Ensure the content wrapper respects the container's constraints
        maxWidth: "100%",
        overflow: "hidden",
        wordWrap: "break-word",
        overflowWrap: "break-word",
      }}
    >
      {renderSafeNode(content)}
    </div>
  );

  return (
    <Tooltip
      mouseEnterDelay={mouseEnterDelay}
      mouseLeaveDelay={mouseLeaveDelay}
      placement={placement}
      className={className}
      autoAdjustOverflow={true}
      overlayClassName={overlayClassName}
      showArrow={true}
      overlayStyle={{
        padding: 0,
      }}
      arrow={false}
      align={{
        offset: [0, -8],
      }}
      afterVisibleChange={handleVisibleChange}
      destroyTooltipOnHide={true}
      motion={{
        motionName: "custom-tooltip-motion",
        motionAppear: true,
        motionEnter: true,
        motionLeave: true,
        onAppearStart: () => ({ opacity: 0, transform: "translateY(8px)" }),
        onAppearActive: () => ({ opacity: 1, transform: "translateY(0)" }),
        onEnterStart: () => ({ opacity: 0, transform: "translateY(8px)" }),
        onEnterActive: () => ({ opacity: 1, transform: "translateY(0)" }),
        onLeaveStart: () => ({ opacity: 1, transform: "translateY(0)" }),
        onLeaveActive: () => ({ opacity: 0, transform: "translateY(8px)" }),
      }}
      overlayInnerStyle={{
        background: token.colorBgContainer,
        boxShadow:
          "0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
        color: token.colorText,
        padding: config.padding,
        fontSize: config.fontSize,
        maxWidth: config.maxWidth,
        minWidth: config.minWidth,
        // Remove fixed width to allow content to determine width
        // width: config.minWidth,
        maxHeight: config.maxHeight,
        borderRadius: config.borderRadius,
        border: `1px solid ${token.colorBorderSecondary}`,
        pointerEvents: "auto",
        opacity: 1,
        transform: "translateY(0)",
        transition: "opacity 300ms ease-in-out, transform 300ms ease-in-out",
        // Add overflow handling
        overflow: "auto",
        overflowX: "hidden",
        // Ensure content wraps properly
        wordWrap: "break-word",
        overflowWrap: "break-word",
      }}
      title={tooltipContent}
      {...otherProps}
    >
      <div style={{ alignItems: "center", gap: "4px" }}>
        {children}
        {showIcon && content && (
          <InfoCircleOutlined
            style={{
              fontSize: "14px",
              padding: "0 8px",
              color: token.colorTextQuaternary,
              opacity: 0.8,
            }}
          />
        )}
      </div>
    </Tooltip>
  );
}
